import { useEffect, useMemo, useState } from 'react';
import {
  api,
  ApiFailure,
  type ConnectorDescriptor,
  type ParameterField,
  type Resource,
  type UploadResult,
} from '../api';
import { Notice } from './Panels';
import { Upload } from './Upload';

/**
 * Where data comes from.
 *
 * Two things this screen does that its predecessor did not. It never shows a
 * source as "Connected" until a credential has actually been exchanged with the
 * provider and come back with a list of things it can see — DataVantageHub's
 * gallery showed a green badge for connectors whose implementation was a
 * hardcoded array. And the setup form carries each field's real help text,
 * because "Invalid credentials" is what you get when nobody told you the
 * service account also has to be added as a user on the property.
 */

const GLYPHS: Record<string, string> = {
  'google-search-console': 'GSC',
  'google-bigquery': 'BQ',
  'google-sheets': 'GS',
  'meta-ads': 'M',
};

type Stage = 'gallery' | 'connect';

export function Sources({
  onLoaded,
  onOpenDataset,
}: {
  onLoaded: (result: UploadResult) => void;
  onOpenDataset: (datasetId: string, reportId: string) => void;
}) {
  const [connectors, setConnectors] = useState<ConnectorDescriptor[]>([]);
  const [failure, setFailure] = useState<ApiFailure | null>(null);
  const [stage, setStage] = useState<Stage>('gallery');
  const [active, setActive] = useState<ConnectorDescriptor | null>(null);

  useEffect(() => {
    api
      .listConnectors()
      .then((response) => setConnectors(response.connectors))
      .catch((error: unknown) => {
        if (error instanceof ApiFailure) setFailure(error);
      });
  }, []);

  if (stage === 'connect' && active !== null) {
    return (
      <ConnectorSetup
        connector={active}
        onCancel={() => {
          setStage('gallery');
          setActive(null);
        }}
        onImported={onOpenDataset}
      />
    );
  }

  return (
    <div className="page">
      <header className="intro">
        <h2>Sources</h2>
        <p>
          Upload a file, or pull from a platform you already use. Everything lands in the same shape, so
          a BigQuery table and a spreadsheet get the same profiling, the same charts and the same
          forecasts.
        </p>
      </header>

      {failure !== null && <Notice tone="error" message={failure.message} remedy={failure.remedy} />}

      <Upload onLoaded={onLoaded} />

      <header className="intro" style={{ marginTop: 8 }}>
        <h2 style={{ fontSize: 16 }}>Connect a platform</h2>
        <p style={{ fontSize: 13.5 }}>
          Credentials are used for the request that needs them and are never stored or sent back to the
          browser. That also means there is no background sync yet — imports are on demand.
        </p>
      </header>

      <div className="source-grid">
        {connectors.map((connector) => (
          <article className="source-card" key={connector.id}>
            <div className="top">
              <div className="glyph">{GLYPHS[connector.id] ?? connector.name.slice(0, 2)}</div>
              <div style={{ minWidth: 0 }}>
                <div className="name">{connector.name}</div>
                <div className="vendor">{connector.vendor}</div>
              </div>
            </div>

            <p>{connector.summary}</p>

            <div className="row tight">
              {connector.timeSeries && <span className="badge accent">date range</span>}
              {connector.scopes.slice(0, 1).map((scope) => (
                <span className="badge" key={scope} title={connector.scopes.join('\n')}>
                  {scope.split('/').pop()}
                </span>
              ))}
            </div>

            <footer>
              <button
                className="primary"
                onClick={() => {
                  setActive(connector);
                  setStage('connect');
                }}
              >
                Connect
              </button>
              <a href={connector.docsUrl} target="_blank" rel="noreferrer">
                <button className="ghost">API docs</button>
              </a>
            </footer>
          </article>
        ))}
      </div>
    </div>
  );
}

function ConnectorSetup({
  connector,
  onCancel,
  onImported,
}: {
  connector: ConnectorDescriptor;
  onCancel: () => void;
  onImported: (datasetId: string, reportId: string) => void;
}) {
  const [credentials, setCredentials] = useState<Record<string, string>>({});
  const [parameters, setParameters] = useState<Record<string, string | string[]>>(() => defaults(connector));
  const [resources, setResources] = useState<Resource[] | null>(null);
  const [dates, setDates] = useState(() => defaultRange());
  const [busy, setBusy] = useState<'verify' | 'import' | null>(null);
  const [failure, setFailure] = useState<ApiFailure | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);

  const resourceParameter = useMemo(
    () => connector.parameters.find((p) => p.kind === 'select' && p.options === undefined),
    [connector],
  );

  async function verify(): Promise<void> {
    setBusy('verify');
    setFailure(null);
    try {
      const response = await api.verifyConnector(connector.id, credentials);
      setResources(response.resources);

      // Pre-select the only thing it can see; a one-item dropdown is a click
      // nobody needs to make.
      if (resourceParameter !== undefined && response.resources.length === 1) {
        const only = response.resources[0]!;
        if (only.id !== '') {
          setParameters((prior) => ({ ...prior, [resourceParameter.key]: only.id }));
        }
      }
    } catch (error) {
      setResources(null);
      setFailure(asFailure(error));
    } finally {
      setBusy(null);
    }
  }

  async function runImport(): Promise<void> {
    setBusy('import');
    setFailure(null);
    setWarnings([]);
    try {
      const response = await api.importConnector(connector.id, {
        credentials,
        parameters,
        ...(connector.timeSeries ? { startDate: dates.start, endDate: dates.end } : {}),
      });
      setWarnings(response.warnings);
      onImported(response.dataset.id, response.report.id);
    } catch (error) {
      setFailure(asFailure(error));
    } finally {
      setBusy(null);
    }
  }

  const canImport =
    resources !== null &&
    connector.parameters.every(
      (field) => !field.required || String(parameters[field.key] ?? '').trim() !== '',
    );

  return (
    <div className="page">
      <header className="intro">
        <div className="row">
          <button className="ghost" onClick={onCancel}>
            ← All sources
          </button>
        </div>
        <h2>Connect {connector.name}</h2>
        <p>{connector.summary}</p>
      </header>

      {failure !== null && <Notice tone="error" message={failure.message} remedy={failure.remedy} />}
      {warnings.map((warning, index) => (
        <Notice key={index} message={warning} />
      ))}

      <section className="card">
        <header>
          <h2>1 · Credentials</h2>
          <span className="sub">held for this request only</span>
        </header>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {connector.credentials.map((field) => (
            <label className="field" key={field.key}>
              {field.label}
              {field.kind === 'json' ? (
                <textarea
                  value={credentials[field.key] ?? ''}
                  spellCheck={false}
                  placeholder='{ "type": "service_account", "client_email": "…", "private_key": "…" }'
                  onChange={(e) => setCredentials({ ...credentials, [field.key]: e.target.value })}
                />
              ) : (
                <input
                  type={field.kind === 'password' ? 'password' : 'text'}
                  autoComplete="off"
                  value={credentials[field.key] ?? ''}
                  onChange={(e) => setCredentials({ ...credentials, [field.key]: e.target.value })}
                />
              )}
              {field.help !== '' && <span className="field-help">{field.help}</span>}
            </label>
          ))}
        </div>

        <div className="row" style={{ marginTop: 14 }}>
          <button className="primary" onClick={verify} disabled={busy !== null}>
            {busy === 'verify' ? <span className="spinner" /> : 'Verify access'}
          </button>
          <span className="muted">
            Required scopes: {connector.scopes.join(', ')}
          </span>
        </div>

        {resources !== null && (
          <div style={{ marginTop: 12 }}>
            <Notice
              tone="good"
              message={
                resources.length === 1 && resources[0]!.id === ''
                  ? 'Credential accepted.'
                  : `Credential accepted — ${resources.length} resource(s) visible.`
              }
              remedy={resources[0]?.detail}
            />
          </div>
        )}
      </section>

      <section className="card">
        <header>
          <h2>2 · What to import</h2>
          {!canImport && <span className="sub">verify first</span>}
        </header>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {connector.timeSeries && (
            <div className="row">
              <label className="field">
                From
                <input
                  type="date"
                  value={dates.start}
                  onChange={(e) => setDates({ ...dates, start: e.target.value })}
                />
              </label>
              <label className="field">
                To
                <input
                  type="date"
                  value={dates.end}
                  onChange={(e) => setDates({ ...dates, end: e.target.value })}
                />
              </label>
              <div className="chip-list" style={{ alignSelf: 'flex-end' }}>
                {[28, 90, 365].map((days) => (
                  <button
                    key={days}
                    className="chip"
                    onClick={() => setDates(range(days))}
                    aria-pressed={dates.start === range(days).start}
                  >
                    Last {days}d
                  </button>
                ))}
              </div>
            </div>
          )}

          {connector.parameters.map((field) => (
            <ParameterInput
              key={field.key}
              field={field}
              value={parameters[field.key]}
              resources={field === resourceParameter ? resources : null}
              onChange={(value) => setParameters({ ...parameters, [field.key]: value })}
            />
          ))}
        </div>

        <div className="row" style={{ marginTop: 16 }}>
          <button className="primary" onClick={runImport} disabled={!canImport || busy !== null}>
            {busy === 'import' ? <span className="spinner" /> : 'Import and analyse'}
          </button>
          {busy === 'import' && <span className="muted">Fetching from {connector.vendor}…</span>}
        </div>
      </section>
    </div>
  );
}

function ParameterInput({
  field,
  value,
  resources,
  onChange,
}: {
  field: ParameterField;
  value: string | string[] | undefined;
  resources: Resource[] | null;
  onChange: (value: string | string[]) => void;
}) {
  // A select whose options come from the account, not from the descriptor.
  if (field.kind === 'select' && field.options === undefined) {
    const usable = (resources ?? []).filter((r) => r.id !== '');
    return (
      <label className="field grow">
        {field.label}
        {usable.length > 0 ? (
          <select value={String(value ?? '')} onChange={(e) => onChange(e.target.value)}>
            <option value="">Choose…</option>
            {usable.map((resource) => (
              <option key={resource.id} value={resource.id}>
                {resource.name}
                {resource.detail === undefined ? '' : ` — ${resource.detail}`}
              </option>
            ))}
          </select>
        ) : (
          <input
            type="text"
            value={String(value ?? '')}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Verify access to list, or paste an id"
          />
        )}
        {field.help !== '' && <span className="field-help">{field.help}</span>}
      </label>
    );
  }

  if (field.kind === 'select') {
    return (
      <label className="field">
        {field.label}
        <select value={String(value ?? '')} onChange={(e) => onChange(e.target.value)}>
          {field.options?.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {field.help !== '' && <span className="field-help">{field.help}</span>}
      </label>
    );
  }

  if (field.kind === 'multiselect') {
    const selected = Array.isArray(value) ? value : [];
    return (
      <div className="field">
        {field.label}
        <div className="chip-list" style={{ marginTop: 3 }}>
          {field.options?.map((option) => {
            const on = selected.includes(option.value);
            return (
              <button
                key={option.value}
                className="chip"
                aria-pressed={on}
                onClick={() =>
                  onChange(
                    on ? selected.filter((v) => v !== option.value) : [...selected, option.value],
                  )
                }
              >
                {option.label}
              </button>
            );
          })}
        </div>
        {field.help !== '' && <span className="field-help">{field.help}</span>}
      </div>
    );
  }

  if (field.kind === 'textarea') {
    return (
      <label className="field grow">
        {field.label}
        <textarea
          value={String(value ?? '')}
          spellCheck={false}
          onChange={(e) => onChange(e.target.value)}
        />
        {field.help !== '' && <span className="field-help">{field.help}</span>}
      </label>
    );
  }

  return (
    <label className="field grow">
      {field.label}
      <input
        type={field.kind === 'number' ? 'number' : 'text'}
        value={String(value ?? '')}
        onChange={(e) => onChange(e.target.value)}
      />
      {field.help !== '' && <span className="field-help">{field.help}</span>}
    </label>
  );
}

function defaults(connector: ConnectorDescriptor): Record<string, string | string[]> {
  const out: Record<string, string | string[]> = {};
  for (const field of connector.parameters) {
    if (field.defaultValue === undefined) continue;
    out[field.key] = Array.isArray(field.defaultValue)
      ? [...field.defaultValue]
      : String(field.defaultValue);
  }
  return out;
}

function range(days: number): { start: string; end: string } {
  const end = new Date();
  // Yesterday, not today: every one of these platforms reports partial or no
  // data for the current day, and a trailing zero looks like a collapse.
  end.setUTCDate(end.getUTCDate() - 1);
  const start = new Date(end);
  start.setUTCDate(start.getUTCDate() - days + 1);
  return { start: iso(start), end: iso(end) };
}

function defaultRange(): { start: string; end: string } {
  return range(90);
}

function iso(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function asFailure(error: unknown): ApiFailure {
  return error instanceof ApiFailure
    ? error
    : new ApiFailure('The request failed.', 'Try again.', 0);
}
